// Shared portfolio data loader.
//
// Why this exists: the Portfolio page used to start fetching /api/portfolio
// only AFTER the visitor clicked "Portfolio", then load ~50 full-size images.
// Now the data is fetched once (in the background, right after the home page
// settles), cached for the whole visit, and the small grid thumbnails are
// pre-downloaded — so clicking Portfolio shows the images immediately.

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SESSION_KEY = 'ar_portfolio_v2';

let cache = null;      // normalized array once loaded
let inflight = null;   // shared promise while loading
let thumbsPreloaded = false;

const abs = (u) => (u && u.startsWith('/uploads/') ? `${API_BASE}${u}` : u);

function normalize(data) {
  return data.map((p) => ({
    ...p,
    src: abs(p.src),
    thumb: abs(p.thumb || p.src),
  }));
}

// Restore from this tab's previous page view (instant on refresh / revisit).
try {
  const saved = sessionStorage.getItem(SESSION_KEY);
  if (saved) cache = JSON.parse(saved);
} catch (_) { /* storage unavailable — fine */ }

/** Synchronous: data already loaded this visit, or null. */
export function getCachedPortfolio() {
  return cache;
}

/** Fetch once and share the result with every caller. Always refreshes in background. */
export function loadPortfolio() {
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/api/portfolio`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
    .then((data) => {
      if (!Array.isArray(data)) throw new Error('bad data');
      cache = normalize(data);
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(cache)); } catch (_) {}
      return cache;
    })
    .catch((err) => {
      inflight = null; // allow a retry later
      if (cache) return cache;
      throw err;
    });
  return inflight;
}

/** First image of each client group — exactly what the grid shows. */
export function firstPerGroup(items) {
  const seen = new Set();
  return items.filter((p) => {
    if (seen.has(p.grp)) return false;
    seen.add(p.grp);
    return true;
  });
}

/** Download the grid thumbnails into the browser cache, a few at a time. */
export function preloadPortfolioThumbs(items) {
  if (thumbsPreloaded || !items) return;
  thumbsPreloaded = true;
  const urls = firstPerGroup(items).map((p) => p.thumb);
  let i = 0;
  const next = () => {
    if (i >= urls.length) return;
    const img = new Image();
    img.decoding = 'async';
    img.onload = img.onerror = next;
    img.src = urls[i++];
  };
  for (let k = 0; k < 4; k++) next(); // 4 parallel lanes
}

/** Warm up the full-size images of one project (e.g. on hover, before click). */
const warmed = new Set();
export function preloadImages(urls) {
  urls.forEach((u) => {
    if (!u || warmed.has(u)) return;
    warmed.add(u);
    const img = new Image();
    img.decoding = 'async';
    img.src = u;
  });
}
